import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2, ArrowLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

import { useMultiStep } from "@/hooks/use-multi-step";
import { useScrollToError } from "@/hooks/use-scroll-to-error";
import { StepIndicator } from "./step-indicator";
import { MultiStepFormProps, FormData } from "./multi-step-form.types";

export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  title,
  description,
  headerContent,
  steps,
  initialData = {},
  onSubmit,
  onStepChange,
  allowClickNavigation = false,
  showStepIndicator = true,
  submitButtonText = "Finalizar",
  nextButtonText = "Próximo",
  previousButtonText = "Anterior",
  saveAsDraftText = "Salvar rascunho",
  showSaveAsDraft = false,
  onSaveAsDraft,
  isSubmitting = false,
  className,
  stepLoadingState,
  customFooter,
  validateAllOnSubmit = false,
  shouldSkipFullValidation,
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      spaceId: "",
      spaceName: "",
      spaceRooms: [],
      name: "",
      size: "",
      capacity: "",
      status: "",
      monthlyPrice: "",
      notes: "",
      photos: [],
      videoUrl: "",
      selectedAmenities: [],
      ...initialData,
    },
    mode: "onChange",
    criteriaMode: "all",
  });

  const { handleSubmit, getValues } = form;
  const { registerField, scrollToFirstError } = useScrollToError();
  const { toast } = useToast();

  // Convert steps to the format expected by useMultiStep
  const stepConfigs = useMemo(
    () =>
      steps.map((step) => ({
        id: step.id,
        title: step.title,
        schema: step.schema,
      })),
    [steps],
  );

  const {
    currentStep,
    completedSteps,
    canGoToNext,
    canGoToPrevious,
    isLastStep,
    goToNext,
    goToPrevious,
    goToStep,
    markStepAsCompleted,
  } = useMultiStep({
    steps: stepConfigs,
    onStepChange,
  });

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const currentStepDef = steps[currentStep];
    if (!currentStepDef) return true;

    try {
      const formData = getValues();

      // If step has custom validation, run it first
      if (currentStepDef.onValidateStep) {
        const customValidation = await currentStepDef.onValidateStep(formData);
        if (!customValidation.valid) {
          if (customValidation.errors) {
            setTimeout(() => {
              scrollToFirstError(customValidation.errors!);
            }, 100);
          }
          return false;
        }
      }

      // Then run schema validation
      const result = currentStepDef.schema.safeParse(formData);

      if (!result.success) {
        const stepErrors: Record<string, string[]> = {};
        result.error.errors.forEach((err) => {
          const field = err.path.join(".");
          if (!stepErrors[field]) stepErrors[field] = [];
          stepErrors[field].push(err.message);
        });

        setTimeout(() => {
          scrollToFirstError(stepErrors);
        }, 100);

        return false;
      }

      return true;
    } catch (error) {
      console.error("Validation error:", error);
      return false;
    }
  }, [currentStep, steps, getValues, scrollToFirstError]);

  const validateAllSteps = useCallback(async (): Promise<{
    valid: boolean;
    firstErrorStep?: number;
    errors?: Record<string, string[]>;
  }> => {
    try {
      const formData = getValues();
      let firstErrorStep: number | undefined;
      const allErrors: Record<string, string[]> = {};
      const newStepsWithErrors = new Set<number>();

      for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
        const stepDef = steps[stepIndex];
        let stepHasErrors = false;

        if (stepDef.onValidateStep) {
          const customValidation = await stepDef.onValidateStep(formData);
          if (!customValidation.valid) {
            stepHasErrors = true;
            if (customValidation.errors) {
              Object.assign(allErrors, customValidation.errors);
            }
          }
        }

        const result = stepDef.schema.safeParse(formData);
        if (!result.success) {
          stepHasErrors = true;
          result.error.errors.forEach((err) => {
            const field = err.path.join(".");
            if (!allErrors[field]) allErrors[field] = [];
            allErrors[field].push(err.message);
          });
        }

        if (stepHasErrors) {
          newStepsWithErrors.add(stepIndex);
          if (firstErrorStep === undefined) {
            firstErrorStep = stepIndex;
          }
        }
      }

      const hasErrors = Object.keys(allErrors).length > 0;

      return {
        valid: !hasErrors,
        firstErrorStep,
        errors: hasErrors ? allErrors : undefined,
      };
    } catch (error) {
      console.error("All steps validation error:", error);
      return { valid: false };
    }
  }, [steps, getValues]);

  const validateStepErrors = useCallback(async () => {
    const formData = getValues();
    const newStepsWithErrors = new Set<number>();

    // Validate each step individually for visual feedback
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      const stepDef = steps[stepIndex];
      let stepHasErrors = false;

      // Schema validation (fast)
      const result = stepDef.schema.safeParse(formData);
      if (!result.success) {
        stepHasErrors = true;
      }

      if (stepHasErrors) {
        newStepsWithErrors.add(stepIndex);
      }
    }

    setStepsWithErrors(newStepsWithErrors);
  }, [steps, getValues]);

  const handleNext = useCallback(async () => {
    setHasAttemptedNext(true);
    const isValid = await validateCurrentStep();
    if (isValid) {
      markStepAsCompleted(currentStep);
      setHasAttemptedNext(false);
      goToNext();
      // Scroll to top after changing step
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validateCurrentStep, markStepAsCompleted, currentStep, goToNext]);

  const onFormSubmit = useCallback(
    async (data: FormData) => {
      setHasAttemptedNext(true);

      const skipFullValidation = shouldSkipFullValidation?.(data) ?? false;

      if (validateAllOnSubmit && !skipFullValidation) {
        const allStepsValidation = await validateAllSteps();

        if (!allStepsValidation.valid) {
          toast({
            variant: "destructive",
            title: "Campos obrigatórios não preenchidos",
            description:
              "Existem campos obrigatórios em algumas seções que precisam ser preenchidos.",
          });

          if (allStepsValidation.firstErrorStep !== undefined) {
            goToStep(allStepsValidation.firstErrorStep);

            setTimeout(() => {
              if (allStepsValidation.errors) {
                scrollToFirstError(allStepsValidation.errors);
              }
            }, 300);
          }

          return;
        }

        for (let i = 0; i < steps.length; i++) {
          markStepAsCompleted(i);
        }
      } else if (validateAllOnSubmit && skipFullValidation) {
        for (let i = 0; i < steps.length; i++) {
          markStepAsCompleted(i);
        }
      } else {
        const isValid = await validateCurrentStep();
        if (isValid) {
          markStepAsCompleted(currentStep);
        } else {
          return;
        }
      }

      await onSubmit(data);
    },
    [
      validateAllOnSubmit,
      shouldSkipFullValidation,
      validateAllSteps,
      validateCurrentStep,
      markStepAsCompleted,
      currentStep,
      onSubmit,
      toast,
      goToStep,
      scrollToFirstError,
      steps.length,
    ],
  );

  const handleStepClick = (step: number) => {
    if (allowClickNavigation) {
      goToStep(step);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (step <= currentStep || completedSteps.has(step)) {
      goToStep(step);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const currentStepDef = steps[currentStep];
  const CurrentStepComponent = currentStepDef?.component;
  const currentTitle = currentStepDef?.customTitle || title;

  // Determine if step is loading
  const isStepLoading = stepLoadingState?.isLoading || false;
  const stepLoadingText = stepLoadingState?.loadingText || "";

  const [currentStepErrors, setCurrentStepErrors] = useState<
    Record<string, string[]>
  >({});
  const [hasAttemptedNext, setHasAttemptedNext] = useState<boolean>(false);
  const [stepsWithErrors, setStepsWithErrors] = useState<Set<number>>(
    new Set(),
  );

  const validateAndSetErrors = useCallback(() => {
    if (!hasAttemptedNext) {
      setCurrentStepErrors({});
      return;
    }

    const currentStepDef = steps[currentStep];
    if (!currentStepDef) {
      setCurrentStepErrors({});
      return;
    }

    try {
      const formData = getValues();
      const result = currentStepDef.schema.safeParse(formData);

      if (!result.success) {
        const stepErrors: Record<string, string[]> = {};
        result.error.errors.forEach((err) => {
          const field = err.path.join(".");
          if (!stepErrors[field]) stepErrors[field] = [];
          stepErrors[field].push(err.message);
        });
        setCurrentStepErrors(stepErrors);
      } else {
        setCurrentStepErrors({});
      }
    } catch (error) {
      console.error("Error validation:", error);
      setCurrentStepErrors({});
    }
  }, [currentStep, steps, getValues, hasAttemptedNext]);

  // Only validate when hasAttemptedNext changes, not on every form data change
  useEffect(() => {
    if (hasAttemptedNext) {
      validateAndSetErrors();
    }
  }, [hasAttemptedNext, validateAndSetErrors]);

  // Re-validate when form data changes if user has already attempted to proceed
  const formValues = form.watch();
  useEffect(() => {
    if (hasAttemptedNext) {
      const timeoutId = setTimeout(() => {
        validateAndSetErrors();
      }, 300); // Debounce validation

      return () => clearTimeout(timeoutId);
    }
  }, [hasAttemptedNext, validateAndSetErrors, formValues]);

  useEffect(() => {
    setCurrentStepErrors({});
    setHasAttemptedNext(false);
  }, [currentStep]);

  // Monitor form changes and update step errors in real-time
  useEffect(() => {
    if (validateAllOnSubmit) {
      const subscription = form.watch(() => {
        // Debounce validation to avoid excessive calls
        const timeoutId = setTimeout(() => {
          validateStepErrors();
        }, 500);

        return () => clearTimeout(timeoutId);
      });

      return () => subscription.unsubscribe();
    }
  }, [form.watch, validateStepErrors, validateAllOnSubmit]);

  return (
    <div className={cn("w-full max-w-4xl mx-auto", className)}>
      {(currentTitle || description) && (
        <div className="text-left mb-12">
          {currentTitle && (
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {currentTitle}
            </h1>
          )}
          {description && (
            <p className="text-muted-foreground text-lg">{description}</p>
          )}
        </div>
      )}

      {headerContent && <div className="mb-6">{headerContent}</div>}

      {showStepIndicator &&
        (() => {
          // Filter out review step from step indicator
          const visibleSteps = stepConfigs.filter(
            (step) => step.id !== "review",
          );

          // Hide step indicator completely when in review step
          const currentStepConfig = stepConfigs[currentStep];
          const isReviewStep = currentStepConfig?.id === "review";

          if (isReviewStep) {
            return null; // Don't show step indicator in review step
          }

          // Adjust currentStep index for filtered steps
          const adjustedCurrentStep =
            currentStep >= visibleSteps.length
              ? visibleSteps.length - 1
              : currentStep;

          return (
            <div className="mb-12">
              <StepIndicator
                steps={visibleSteps}
                currentStep={adjustedCurrentStep}
                completedSteps={completedSteps}
                onStepClick={allowClickNavigation ? handleStepClick : undefined}
                allowClickNavigation={allowClickNavigation}
                stepsWithErrors={stepsWithErrors}
              />
            </div>
          );
        })()}

      <div className="mb-24">
        <FormProvider {...form}>
          {CurrentStepComponent && (
            <CurrentStepComponent
              errors={currentStepErrors}
              registerField={registerField}
              onGoToStep={goToStep}
            />
          )}

          {Object.keys(currentStepErrors).length > 0 && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800 font-medium mb-2">
                Corrija os seguintes erros:
              </p>
              <ul className="text-sm text-red-700 space-y-1">
                {Object.entries(currentStepErrors).map(([field, messages]) =>
                  messages.map((message, index) => (
                    <li key={`${field}-${index}`}>• {message}</li>
                  )),
                )}
              </ul>
            </div>
          )}
        </FormProvider>
      </div>

      {customFooter ? (
        // Use custom footer if provided
        <form id="multi-step-form" onSubmit={handleSubmit(onFormSubmit)}>
          {customFooter}
        </form>
      ) : (
        // Default footer
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-4 z-50">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div className="flex gap-2">
              {showSaveAsDraft && onSaveAsDraft && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onSaveAsDraft(getValues())}
                  disabled={isSubmitting}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {saveAsDraftText}
                </Button>
              )}
            </div>

            <div className="flex gap-3">
              {currentStep > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    goToPrevious();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  disabled={!canGoToPrevious || isSubmitting}
                  className="h-12 border-gray-300 text-gray-700 hover:bg-gray-50 px-6 rounded-lg"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  {previousButtonText}
                </Button>
              )}
              {!isLastStep ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!canGoToNext || isSubmitting || isStepLoading}
                  className="h-12 rounded-lg bg-[#F9E10D] text-black hover:bg-[#F9E10D]/80"
                >
                  {isStepLoading && (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  )}
                  {isStepLoading ? stepLoadingText : nextButtonText}
                  {!isStepLoading && (
                    <ChevronRight size={16} className="ml-2" />
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit(onFormSubmit)}
                  disabled={isSubmitting}
                  className="h-12 rounded-lg bg-[#F9E10D] text-black hover:bg-[#F9E10D]/80"
                >
                  {isSubmitting && (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  )}
                  {submitButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
